import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { AuthContext } from "../../context/AuthContextProvider";
import { fetcherGET, endpoints } from "../../utils/axios";
import StateDistrictFilter from "../../components/Dashboard/geographyFilter";
import { exportAllExcel } from "./exportUtils";
import {
  SummaryCard,
  DailyVolumeCard,
  DetailTableCard,
} from "./AnalyticsCards";
import {
  KpiBand,
  OverviewCharts,
  CentreCharts,
  ClinicalCharts,
  PatientCharts,
} from "./AnalyticsCharts";
import {
  DateFilterSelect,
  buildProgrammeRows,
  buildDailyRows,
  programmeColumns,
  dailyColumns,
  centreColumns,
  hubColumns,
  districtColumns,
  criticalColumns,
  retakeColumns,
  demoColumns,
  caseColumns,
} from "./shared";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "centres", label: "Centres" },
  { key: "clinical", label: "Clinical" },
  { key: "patients", label: "Patients" },
];

/* Labelled divider that separates the visual dashboard from the data tables. */
const SectionHeading = ({ icon, label }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      mt: { xs: 1, md: 1.5 },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        color: "var(--app-text-muted)",
      }}
    >
      <Icon icon={icon} width="18" />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--app-text-muted)",
          fontFamily: "Figtree",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
    <Box
      sx={{ flex: 1, height: "1px", backgroundColor: "var(--app-border)" }}
    />
  </Box>
);

const AnalyticsPage = () => {
  const { all_centres } = useContext(AuthContext);

  // Persist the chosen filters so they survive navigating away and back
  // (kept for the browser session; cleared when the tab is closed).
  const saved = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("analytics:filters")) || {};
    } catch {
      return {};
    }
  }, []);

  const [geo, setGeo] = useState(saved.geo || { state: "", district: "" });
  const [selectedCentres, setSelectedCentres] = useState(
    saved.selectedCentres || [],
  ); // [] = all
  const [search, setSearch] = useState("");

  const [tab, setTab] = useState(saved.tab ?? 0);

  // Master date filter: drives the visual dashboard, syncs every card's date,
  // and is the range used by "Export All". Cards can still override locally.
  const [globalDate, setGlobalDate] = useState(
    saved.globalDate || "last_30_days",
  );
  const [exporting, setExporting] = useState(false);

  // Cards still accept a register prop; not used for the global export.
  const register = useCallback(() => {}, []);

  const allCentres = useMemo(() => {
    if (!Array.isArray(all_centres)) return [];
    return all_centres.flatMap((hub) => {
      const hubOpt = {
        label: hub?.label ?? hub?.name,
        value: hub?.value ?? hub?.id,
      };
      const spokes =
        hub?.spokeCenters?.map((s) => ({
          label: s?.name ?? "Unnamed Spoke",
          value: s?.id,
        })) || [];
      return [hubOpt, ...spokes];
    });
  }, [all_centres]);
  const filteredCentres = useMemo(() => {
    return allCentres.filter((centre) =>
      centre.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [allCentres, search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Save filters on every change so returning to Analytics restores them.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        "analytics:filters",
        JSON.stringify({ geo, selectedCentres, tab, globalDate }),
      );
    } catch {
      /* ignore */
    }
  }, [geo, selectedCentres, tab, globalDate]);

  const isAllCentres =
    selectedCentres.length === 0 ||
    selectedCentres.length === allCentres.length;

  const filters = useMemo(
    () => ({
      state: geo.state || "",
      district: geo.district || "",
      center_ids: isAllCentres ? null : selectedCentres,
    }),
    [geo.state, geo.district, isAllCentres, selectedCentres],
  );

  // Export All: fetch every table at the chosen GLOBAL date, then export.
  const handleExportAll = async () => {
    try {
      setExporting(true);
      const baseArgs = {
        center_selected: filters.center_ids ? "no" : "all",
        center_ids: filters.center_ids || null,
        granularity: "day",
        cases_granularity: "day",
        filter: globalDate,
        state: filters.state || "",
        district: filters.district || "",
      };
      const [ins, ts, tbl] = await Promise.all([
        fetcherGET(endpoints.analytics.getInsights, { params: baseArgs }),
        fetcherGET(endpoints.analytics.getTimeseries, { params: baseArgs }),
        fetcherGET(endpoints.analyticsExport.getTables, { params: baseArgs }),
      ]);
      const analytics = ins?.data || {};
      const timeSeries = ts?.data || {};
      const t = tbl?.data || {};
      exportAllExcel("Analytics_Report", [
        {
          title: "Programme Summary",
          columns: programmeColumns,
          rows: buildProgrammeRows(analytics),
        },
        {
          title: "Daily Test Volume & Results",
          columns: dailyColumns,
          rows: buildDailyRows(timeSeries),
        },
        {
          title: "Centre Performance",
          columns: centreColumns,
          rows: t.centrePerformance || [],
        },
        {
          title: "Hub-wise Performance",
          columns: hubColumns,
          rows: t.hubPerformance || [],
        },
        {
          title: "District-wise Performance",
          columns: districtColumns,
          rows: t.districtPerformance || [],
        },
        {
          title: "Critical Cases Detail",
          columns: criticalColumns,
          rows: t.criticalCases || [],
        },
        {
          title: "Retake Analysis",
          columns: retakeColumns,
          rows: t.retakeAnalysis || [],
        },
        {
          title: "Patient Demographics",
          columns: demoColumns,
          rows: t.demographics || [],
        },
        {
          title: "Cases Created Log",
          columns: caseColumns,
          rows: t.casesLog || [],
        },
      ]);
    } catch (e) {
      console.error("Export All failed:", e);
    } finally {
      setExporting(false);
    }
  };

  const panelSx = (idx) => ({
    display: tab === idx ? "flex" : "none",
    flexDirection: "column",
    gap: { xs: 2, md: 2.5 },
  });

  return (
    <>
      <Helmet>
        <title>Analytics - Spandan One</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box
          className="page-wrapper"
          sx={{
            maxHeight: "99vh",
            overflowY: "auto",
            pb: "48px",
            pt: "65px",
            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <Box className="content">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, md: 2.5 },
                pb: { xs: 4, md: 6 },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontFamily: "Figtree",
                      fontSize: "24px",
                      color: "var(--app-text)",
                      lineHeight: 1.2,
                    }}
                  >
                    Analytics
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "var(--app-text-muted)",
                      fontFamily: "Figtree",
                    }}
                  >
                    Visual dashboards & deep tabular intelligence across
                    centres, tests, patients & outcomes.
                  </Typography>
                </Box>

                <Button
                  onClick={handleExportAll}
                  disabled={exporting}
                  startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 14,
                    fontFamily: "Figtree",
                    color: "#fff",
                    backgroundColor: "#5751E9",
                    borderRadius: "10px",
                    px: 2,
                    py: 1,
                    boxShadow: "var(--app-shadow)",
                    "&:hover": { backgroundColor: "#4B45D6" },
                    "&.Mui-disabled": {
                      backgroundColor: "#C7C5F5",
                      color: "#fff",
                    },
                  }}
                >
                  {exporting ? "Exporting…" : "Export All"}
                </Button>
              </Box>

              {/* Global filters (date / state / district / centre). The date here is
                  the master range — it drives the charts and syncs every card. */}
              <Box
                sx={{
                  backgroundColor: "var(--app-surface)",
                  border: "1px solid var(--app-border)",
                  borderRadius: "16px",
                  boxShadow: "var(--app-shadow)",
                  px: { xs: 1.5, md: 2 },
                  py: 1.25,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--app-text-muted)",
                    mr: 0.5,
                  }}
                >
                  Filters
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pr: 1.5,
                    mr: 0.5,
                    borderRight: { sm: "1px solid var(--app-border)" },
                  }}
                >
                  <Icon
                    icon="mdi:calendar-range-outline"
                    width="18"
                    color="#5751E9"
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "var(--app-text)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Date Range
                  </Typography>
                  <DateFilterSelect
                    value={globalDate}
                    onChange={setGlobalDate}
                  />
                </Box>

                <Box sx={{ minWidth: 150 }}>
                  <StateDistrictFilter
                    key={`${geo.state}-${geo.district}`}
                    onChange={(s, d) => setGeo({ state: s, district: d })}
                    initialState={geo.state}
                    initialDistrict={geo.district}
                  />
                </Box>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    backgroundColor: "var(--app-surface)",
                    border: "1px solid var(--app-border)",
                    borderRadius: "8px",
                    height: 40,
                    justifyContent: "center",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  <Select
                    multiple
                    displayEmpty
                    value={selectedCentres}
                    onOpen={() => setSearch("")}
                    onChange={(e) => {
                      const values = e.target.value;

                      if (values.includes("all")) {
                        if (selectedCentres.length === allCentres.length) {
                          setSelectedCentres([]);
                        } else {
                          setSelectedCentres(allCentres.map((c) => c.value));
                        }
                        return;
                      }

                      setSelectedCentres(values);
                    }}
                    renderValue={(selected) => {
                      if (
                        selected.length === 0 ||
                        selected.length === allCentres.length
                      ) {
                        return "All Centres";
                      }

                      if (selected.length === 1) {
                        return allCentres.find((c) => c.value === selected[0])
                          ?.label;
                      }

                      return `${selected.length} selected`;
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 48 * 8, // shows 8 items only
                          overflowY: "auto",
                          mt: 0.5,
                          borderRadius: "12px",
                          border: "1px solid var(--app-border)",
                          boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
                          "& .MuiMenuItem-root": {
                            fontSize: 14,
                            fontFamily: "figtree",
                          },
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      MenuListProps: {
                        autoFocusItem: false,
                      },
                    }}
                    sx={{
                      width: "100%",
                      height: 40,

                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },

                      "& .MuiSelect-select": {
                        display: "flex",
                        alignItems: "center",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "var(--app-text)",
                        padding: "8px 12px",
                      },

                      "& .MuiSelect-icon": {
                        right: 10,
                      },
                    }}
                  >
                    {/* Search */}
                    <Box
                      sx={{
                        p: 1,
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        background: "var(--app-surface)",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Search Centre"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 38,
                            borderRadius: "12px",
                            backgroundColor: "var(--app-surface)",

                            "& input": {
                              padding: "12px 0px",
                              fontSize: "15px",
                              fontWeight: 400,
                              color: "var(--app-text)",
                            },

                            "& input::placeholder": {
                              color: "var(--app-text-muted)",
                              opacity: 1,
                              fontSize: "15px",
                            },

                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid var(--app-border)",
                            },

                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid var(--app-border)",
                            },

                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid var(--app-border)",
                            },

                            "&.Mui-focused": {
                              boxShadow: "none",
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{
                                  color: "var(--app-text-muted)",
                                  fontSize: 24,
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* All Centres */}
                    <MenuItem
                      value="all"
                      sx={{
                        minHeight: 42,

                        "&.Mui-selected": {
                          backgroundColor: "transparent !important",
                        },

                        "&.Mui-selected:hover": {
                          backgroundColor: "transparent !important",
                        },

                        "&:hover": {
                          backgroundColor: "var(--app-surface-2)",
                        },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedCentres.length === allCentres.length}
                        indeterminate={
                          selectedCentres.length > 0 &&
                          selectedCentres.length < allCentres.length
                        }
                        sx={{
                          color: "var(--app-border)",

                          "&.Mui-checked": {
                            color: "#5751E9",
                          },

                          "& .MuiSvgIcon-root": {
                            borderRadius: "8px",
                            fontSize: 20,
                          },
                        }}
                      />
                      <ListItemText
                        primary="All Centres"
                        primaryTypographyProps={{
                          fontSize: 14,
                          noWrap: true,
                        }}
                      />
                    </MenuItem>

                    {/* Centre List */}
                    {filteredCentres.length ? (
                      filteredCentres.map((centre) => (
                        <MenuItem
                          key={centre.value}
                          value={centre.value}
                          sx={{
                            minHeight: 42,

                            "&.Mui-selected": {
                              backgroundColor: "transparent !important",
                            },

                            "&.Mui-selected:hover": {
                              backgroundColor: "transparent !important",
                            },

                            "&:hover": {
                              backgroundColor: "var(--app-surface-2)",
                            },
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedCentres.includes(centre.value)}
                            sx={{
                              color: "var(--app-border)",

                              "&.Mui-checked": {
                                color: "#5751E9",
                              },

                              "& .MuiSvgIcon-root": {
                                borderRadius: "8px",
                                fontSize: 20,
                              },
                            }}
                          />
                          <ListItemText
                            primary={centre.label}
                            primaryTypographyProps={{
                              fontSize: 14,
                              noWrap: true,
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <ListItemText
                          primary="No results found"
                          primaryTypographyProps={{
                            fontSize: 14,
                            color: "var(--app-text-muted)",
                          }}
                        />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {(geo.state || selectedCentres.length > 0) && (
                  <Button
                    onClick={() => {
                      setGeo({ state: "", district: "" });
                      setSelectedCentres([]);
                    }}
                    startIcon={<Icon icon="mdi:close" width="14" />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#5751E9",
                      ml: "auto",
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>

              {/* KPI band — animated headline numbers for the master range */}
              <KpiBand filters={filters} date={globalDate} />

              {/* Tabs */}
              <Box
                sx={{
                  backgroundColor: "var(--app-surface)",
                  border: "1px solid var(--app-border)",
                  borderRadius: "12px",
                  px: 1,
                  boxShadow: "var(--app-shadow)",
                }}
              >
                <Tabs
                  value={tab}
                  onChange={(e, v) => setTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: "Figtree",
                      fontSize: 14,
                      minHeight: 44,
                      color: "var(--app-text-muted)",
                    },
                    "& .Mui-selected": { color: "#5751E9 !important" },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#5751E9",
                      height: 3,
                    },
                  }}
                >
                  {TABS.map((t) => (
                    <Tab key={t.key} label={t.label} />
                  ))}
                </Tabs>
              </Box>

              {/* Panels (all mounted so Export All has every table).
                  Each tab leads with its graphical dashboard, then the full
                  data tables underneath (all original tables retained). */}
              <Box sx={panelSx(0)}>
                <OverviewCharts filters={filters} date={globalDate} />
                <SectionHeading
                  icon="mdi:table-large"
                  label="Detailed Tables"
                />
                <SummaryCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                />
                <DailyVolumeCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                />
              </Box>

              <Box sx={panelSx(1)}>
                <CentreCharts filters={filters} date={globalDate} />
                <SectionHeading
                  icon="mdi:table-large"
                  label="Detailed Tables"
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="centre"
                  tableKey="centrePerformance"
                  title="Centre Performance Summary"
                  subtitle="Per-centre scorecard including inactive & dormant centres."
                  columns={centreColumns}
                  fileName="Centre_Performance"
                  getRowSx={(r) =>
                    r.status === "Dormant"
                      ? { backgroundColor: "rgba(211, 47, 47, 0.14)" }
                      : r.status === "Never Used"
                        ? { backgroundColor: "rgba(148, 163, 184, 0.10)" }
                        : undefined
                  }
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="hub"
                  tableKey="hubPerformance"
                  title="Centre Wise"
                  subtitle="Rollup of centres grouped by their Hub centre."
                  columns={hubColumns}
                  fileName="Hub_Performance"
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="district"
                  tableKey="districtPerformance"
                  title="District-wise Performance"
                  subtitle="Centres, tests & outcomes rolled up by centre district."
                  columns={districtColumns}
                  fileName="District_Performance"
                />
              </Box>

              <Box sx={panelSx(2)}>
                <ClinicalCharts filters={filters} date={globalDate} />
                <SectionHeading
                  icon="mdi:table-large"
                  label="Detailed Tables"
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="critical"
                  tableKey="criticalCases"
                  title="Critical Cases Detail"
                  subtitle="Every critical ECG report — primary clinical review."
                  columns={criticalColumns}
                  fileName="Critical_Cases"
                  getRowSx={(r) =>
                    Number(r.bpm) < 40 || Number(r.bpm) > 150
                      ? { backgroundColor: "rgba(211, 47, 47, 0.12)" }
                      : undefined
                  }
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="retake"
                  tableKey="retakeAnalysis"
                  title="Retake Analysis"
                  subtitle="Retake burden by centre with root-cause breakdown."
                  columns={retakeColumns}
                  fileName="Retake_Analysis"
                  getRowSx={(r) =>
                    r.retakePct > 25
                      ? { backgroundColor: "rgba(211, 47, 47, 0.12)" }
                      : undefined
                  }
                />
              </Box>

              <Box sx={panelSx(3)}>
                <PatientCharts filters={filters} date={globalDate} />
                <SectionHeading
                  icon="mdi:table-large"
                  label="Detailed Tables"
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="demographics"
                  tableKey="demographics"
                  title="Patient Demographics Breakdown"
                  subtitle="Age group × gender distribution and risk."
                  columns={demoColumns}
                  fileName="Patient_Demographics"
                />
                <DetailTableCard
                  filters={filters}
                  register={register}
                  syncDate={globalDate}
                  regKey="cases"
                  tableKey="casesLog"
                  title="Cases Created Log"
                  subtitle="Every case created from a critical or escalated test."
                  columns={caseColumns}
                  fileName="Cases_Created_Log"
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AnalyticsPage;
